import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject,
} from '@angular/core';
import { TranslatePipe, TranslationService } from '@kirigami/shared/i18n';

type CardSlug = (typeof GROUPS)[number]['cards'][number];

/**
 * One facet of an icon on a 48-unit grid. Tones 1 to 3 are the light, middle
 * and deep step of the icon's hue; `cut` is a hole in the paper.
 */
type IconShape = readonly [tone: 1 | 2 | 3 | 'cut', d: string];

/** The brand ramps the icons are folded from, one per icon. */
type IconHue = 'pink' | 'tree' | 'coral' | 'blue' | 'purple';

interface WhyIcon {
  readonly hue: IconHue;
  readonly shapes: readonly IconShape[];
}

/** One item under a pillar, its copy resolved for the active locale. */
interface WhyCard {
  readonly slug: CardSlug;
  readonly title: string;
  readonly body: string;
  readonly icon: WhyIcon;
}

interface WhyGroup {
  readonly slug: (typeof GROUPS)[number]['slug'];
  readonly title: string;
  readonly cards: readonly WhyCard[];
}

/**
 * The pillars in design order, each with its items. The slugs address the copy
 * under `landing.features.pillars.<group>` and `landing.features.cards.<card>`.
 */
const GROUPS = [
  { slug: 'trust', cards: ['safety', 'authorship', 'noMagic'] },
  { slug: 'collaboration', cards: ['share', 'assign'] },
  { slug: 'method', cards: ['reliability', 'learn', 'library'] },
] as const;

/**
 * Folded-paper icons in the style of the earlier ones (owner 2026-11-04): two
 * to four flat facets of one brand hue, creased in the background colour, so
 * they stay light next to the text. Each still says what its item is about: a
 * shield, a pen nib, a tick (you approve every change), a paper plane, a card
 * handed to a person, a set square (the framework), a mortarboard and books.
 * The hues alternate so no row or column repeats one.
 */
const ICONS: Record<CardSlug, WhyIcon> = {
  safety: {
    hue: 'pink',
    shapes: [
      [2, 'M24 5 L8 10 L24 24 Z'],
      [1, 'M8 10 L10 30 L24 44 L24 24 Z'],
      [3, 'M24 5 L40 10 L38 30 L24 44 Z'],
    ],
  },
  authorship: {
    hue: 'tree',
    shapes: [
      [1, 'M16 5 L24 5 L24 44 L11 22 Z'],
      [3, 'M24 5 L32 5 L37 22 L24 22 Z'],
      [2, 'M24 22 L37 22 L24 44 Z'],
    ],
  },
  noMagic: {
    hue: 'purple',
    shapes: [
      [2, 'M5 25 L11 19 L19 27 L19 39 Z'],
      [3, 'M19 27 L37 8 L43 14 L19 39 Z'],
    ],
  },
  share: {
    hue: 'blue',
    shapes: [
      [1, 'M44 6 L4 22 L17 28 Z'],
      [2, 'M44 6 L17 28 L22 42 Z'],
      [3, 'M17 28 L22 42 L26 33 Z'],
    ],
  },
  assign: {
    hue: 'purple',
    shapes: [
      [1, 'M4 5 L22 5 L28 11 L28 37 L4 37 Z'],
      [2, 'M22 5 L22 11 L28 11 Z'],
      [3, 'M36 15 L42 21 L36 27 L30 21 Z'],
      [2, 'M24 45 L36 30 L36 45 Z'],
      [3, 'M36 30 L48 45 L36 45 Z'],
    ],
  },
  reliability: {
    hue: 'coral',
    shapes: [
      [1, 'M6 5 L6 43 L22 43 Z'],
      [3, 'M6 5 L22 43 L44 43 Z'],
      ['cut', 'M12 21 L12 37 L28 37 Z'],
    ],
  },
  learn: {
    hue: 'pink',
    shapes: [
      [1, 'M24 8 L45 16 L24 24 L3 16 Z'],
      [2, 'M12 20 L24 25 L36 20 L36 32 L24 37 L12 32 Z'],
      [3, 'M38 18 L41 18 L42 30 L37 30 Z'],
    ],
  },
  library: {
    hue: 'blue',
    shapes: [
      [1, 'M6 10 L14 10 L14 42 L6 42 Z'],
      [2, 'M16 6 L25 6 L25 42 L16 42 Z'],
      [3, 'M28 13 L36 11 L44 40 L36 42 Z'],
    ],
  },
};

/**
 * "Why researchers choose Kirigami": the six original reasons plus two on
 * collaboration, grouped under three pillars (Trust, Collaboration, Method).
 * Each item has its own icon, a short title and one line.
 *
 * Each pillar rises into place once, the first time it enters the viewport.
 * The reveal only arms when an IntersectionObserver exists and motion is
 * allowed; otherwise everything renders visible from the start.
 */
@Component({
  selector: 'app-features-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    <section id="why" aria-labelledby="why-title" class="why">
      <div class="why__inner">
        <h2 id="why-title" class="why__title" data-reveal>{{ 'landing.features.title' | t }}</h2>
        <div class="why__groups">
          @for (group of groups(); track group.slug; let i = $index) {
            <div
              class="why__group"
              [attr.data-pillar]="group.slug"
              data-reveal
              [style.--d]="i * 0.12 + 's'"
            >
              <h3 class="why__group-title">{{ group.title }}</h3>
              <ul class="why__items">
                @for (card of group.cards; track card.slug) {
                  <li class="why__item" [attr.data-card]="card.slug">
                    <svg
                      class="why__icon"
                      [attr.data-hue]="card.icon.hue"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                      focusable="false"
                    >
                      @for (shape of card.icon.shapes; track $index) {
                        <path [class]="'ico ico-' + shape[0]" [attr.d]="shape[1]" />
                      }
                    </svg>
                    <div class="why__text">
                      <h4 class="why__item-title">{{ card.title }}</h4>
                      <p class="why__item-body">{{ card.body }}</p>
                    </div>
                  </li>
                }
              </ul>
            </div>
          }
        </div>
        <hr class="why__divider" aria-hidden="true" />
      </div>
    </section>
  `,
  styles: [
    `
      .why {
        background: var(--kirigami-50);
        padding: 6.875rem 1.5rem 0;
      }
      @media (min-width: 1024px) {
        .why {
          padding: 6.875rem 4.0625rem 0;
        }
      }
      .why__inner {
        max-width: 81.875rem;
        margin: 0 auto;
      }
      .why__title {
        margin: 0 auto 5rem;
        max-width: 22ch;
        text-align: center;
        font-size: clamp(2rem, 4vw, 2.5rem);
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: -0.011em;
        color: var(--neu-900);
        text-wrap: balance;
      }
      @media (min-width: 1024px) {
        .why__title {
          max-width: none;
        }
      }
      .why__groups {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 4rem;
        max-width: 34rem;
        margin: 0 auto;
      }
      @media (min-width: 1024px) {
        .why__groups {
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 4.5rem;
          max-width: none;
        }
      }
      .why__group-title {
        margin: 0;
        padding-bottom: 1rem;
        border-bottom: 1px solid var(--kirigami-200);
        font-size: 1.75rem;
        font-weight: 700;
        line-height: 1.2;
        letter-spacing: -0.01em;
        color: var(--kirigami-700);
      }
      .why__items {
        display: flex;
        flex-direction: column;
        gap: 2.25rem;
        margin: 2.25rem 0 0;
        padding: 0;
        list-style: none;
      }
      .why__item {
        display: grid;
        grid-template-columns: 2.75rem minmax(0, 1fr);
        column-gap: 1.25rem;
        align-items: start;
      }
      .why__icon {
        display: block;
        width: 2.75rem;
        height: 2.75rem;
      }
      .why__item-title {
        margin: 0.125rem 0 0;
        font-size: 1.125rem;
        font-weight: 700;
        line-height: 1.35;
        color: var(--neu-900);
      }
      .why__item-body {
        margin: 0.375rem 0 0;
        font-size: 1rem;
        line-height: 1.6;
        color: var(--neu-800);
        text-wrap: pretty;
      }
      .why__divider {
        margin: 7.375rem 0 0;
        border: 0;
        border-top: 1px dashed var(--kirigami-300);
      }

      .why__icon[data-hue='pink'] {
        --t1: var(--pink-300);
        --t2: var(--pink-400);
        --t3: var(--pink-500);
      }
      .why__icon[data-hue='tree'] {
        --t1: var(--tree-200);
        --t2: var(--tree-300);
        --t3: var(--tree-400);
      }
      .why__icon[data-hue='coral'] {
        --t1: var(--coral-300);
        --t2: var(--coral-400);
        --t3: var(--coral-500);
      }
      .why__icon[data-hue='blue'] {
        --t1: var(--kirigami-300);
        --t2: var(--kirigami-400);
        --t3: var(--kirigami-500);
      }
      .why__icon[data-hue='purple'] {
        --t1: var(--purple-400);
        --t2: var(--purple-500);
        --t3: var(--purple-600);
      }
      .ico {
        stroke: var(--kirigami-50);
        stroke-width: 1.2;
        stroke-linejoin: round;
      }
      .ico-1 {
        fill: var(--t1);
      }
      .ico-2 {
        fill: var(--t2);
      }
      .ico-3 {
        fill: var(--t3);
      }
      .ico-cut {
        fill: var(--kirigami-50);
        stroke: none;
      }

      /* Armed by the component once the observer runs, so a missing observer
         or reduced motion leaves everything visible. Transform and opacity
         only: the layout never moves. */
      .why--reveal-armed [data-reveal] {
        opacity: 0;
      }
      .why--reveal-armed [data-reveal].is-in {
        animation: why-rise 0.7s cubic-bezier(0.16, 1, 0.3, 1) var(--d, 0s) both;
      }
      @keyframes why-rise {
        from {
          opacity: 0;
          transform: translateY(1rem);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }
    `,
  ],
})
export class FeaturesWidgetComponent implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private observer?: IntersectionObserver;

  private readonly translations = inject(TranslationService);

  /**
   * The pillars with their copy resolved for the active locale. Resolved here
   * rather than in the template because a `@for` cannot name a key literally.
   */
  readonly groups = computed<readonly WhyGroup[]>(() =>
    GROUPS.map((group) => ({
      slug: group.slug,
      title: this.translations.t(`landing.features.pillars.${group.slug}`),
      cards: group.cards.map((slug) => ({
        slug,
        title: this.translations.t(`landing.features.cards.${slug}.title`),
        body: this.translations.t(`landing.features.cards.${slug}.body`),
        icon: ICONS[slug],
      })),
    })),
  );

  ngAfterViewInit(): void {
    if (
      typeof matchMedia === 'undefined' ||
      typeof IntersectionObserver === 'undefined' ||
      matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    const section = this.host.nativeElement.querySelector<HTMLElement>('.why');
    if (!section) return;

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    );

    section.classList.add('why--reveal-armed');
    section.querySelectorAll('[data-reveal]').forEach((el) => this.observer?.observe(el));
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
